/**
 * WebAudio engine: Sounds.txt-driven effects (decoded WAVs from the archives, cached), a music
 * channel with cross-fades, NPC speech, and separate volume buses. Browsers block audio until a
 * user gesture, so the context resumes on the first input.
 *
 * Effects follow the table's rules the way the original mixer did: per-row volume, looping,
 * fade in/out (in game frames), random group variants, "stop instance" (a new start cuts the
 * previous one), "defer instance" (no restart while one plays) and a fixed number of voices where
 * a new sound may only steal a slot from one of lower priority.
 */
import type { ArchiveSet, ExcelTable } from '@d2/formats';
import { type SoundDef, SoundTable } from './sound-table';
import { spatialize } from './spatial';

/** Simultaneous effect voices (the original mixed a fixed set of DirectSound buffers). */
const MAX_VOICES = 32;
/** Sounds.txt fades are counted in game frames. */
const FRAME_S = 1 / 25;

export interface PlayOpts {
  /** Multiplies the row's volume. */
  volume?: number;
  /** -1..1. */
  pan?: number;
  /**
   * Loop the sound. Off unless asked: Sounds.txt marks missile flights, auras and fires as loops
   * the engine stops when their owner goes away, and a fire-and-forget voice (a server event, an
   * impact) has no owner to stop it, so it would repeat for ever. Callers that stop their voices
   * (ambience beds and events, rain) pass `loop` themselves.
   */
  loop?: boolean;
  bus?: GainNode;
}

/** A playing (or about to play) instance; `stop` honours the row's fade-out unless told otherwise. */
export class Voice {
  stopped = false;
  ended = false;
  src: AudioBufferSourceNode | null = null;
  constructor(
    readonly def: SoundDef,
    readonly key: number,
    readonly gain: GainNode,
    private readonly ctx: AudioContext,
  ) {}

  stop(fadeFrames = this.def.fadeOut): void {
    if (this.stopped) return;
    this.stopped = true;
    const now = this.ctx.currentTime;
    const t = Math.max(0.01, fadeFrames * FRAME_S);
    const g = this.gain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0, now + t);
    try {
      this.src?.stop(now + t + 0.02);
    } catch {
      /* not started yet */
    }
  }

  /** Smoothly changes the voice's level (crossfades, weather intensity). */
  setLevel(v: number, seconds = 0.25): void {
    if (this.stopped) return;
    const now = this.ctx.currentTime;
    const g = this.gain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(v, now + seconds);
  }
}

export class AudioEngine {
  readonly ctx: AudioContext;
  private readonly master: GainNode;
  readonly sfxBus: GainNode;
  readonly musicBus: GainNode;
  readonly speechBus: GainNode;
  private readonly buffers = new Map<string, Promise<AudioBuffer | null>>();
  sounds: SoundTable | null = null;
  private readonly voices = new Set<Voice>();
  /** Stereo spread for positional sounds (Sound Options "3D bias"), 0..1. */
  separation = 1;
  private music: { src: AudioBufferSourceNode; gain: GainNode; path: string } | null = null;
  private speech: AudioBufferSourceNode | null = null;

  constructor(private readonly vfs: ArchiveSet) {
    this.ctx = new AudioContext({ latencyHint: 'interactive' });
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    this.sfxBus = this.bus(0.9);
    this.musicBus = this.bus(0.55);
    this.speechBus = this.bus(1);
    const resume = () => {
      if (this.ctx.state !== 'running') void this.ctx.resume();
    };
    for (const ev of ['pointerdown', 'keydown']) window.addEventListener(ev, resume, { capture: true });
  }

  private bus(v: number): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = v;
    g.connect(this.master);
    return g;
  }

  setVolumes(v: { master?: number; sfx?: number; music?: number; speech?: number }): void {
    if (v.master !== undefined) this.master.gain.value = v.master;
    if (v.sfx !== undefined) this.sfxBus.gain.value = v.sfx;
    if (v.music !== undefined) this.musicBus.gain.value = v.music;
    if (v.speech !== undefined) this.speechBus.gain.value = v.speech;
  }

  /** Loads Sounds.txt so effects can be played by row or name (e.g. "cursor_button"). */
  loadSoundTable(t: ExcelTable): void {
    this.sounds = new SoundTable(t);
  }

  sound(nameOrIndex: string | number): SoundDef | undefined {
    if (!this.sounds) return undefined;
    return typeof nameOrIndex === 'number' ? this.sounds.get(nameOrIndex) : this.sounds.find(nameOrIndex);
  }

  buffer(path: string): Promise<AudioBuffer | null> {
    const key = path.toLowerCase();
    let p = this.buffers.get(key);
    if (!p) {
      p = this.vfs
        .tryRead(path)
        .then((d) => (d ? this.ctx.decodeAudioData(d.slice().buffer) : null))
        .catch(() => null);
      this.buffers.set(key, p);
    }
    return p;
  }

  /** Plays a file directly. `pan` -1..1. Returns a stop function. */
  async playFile(path: string, o: { volume?: number; pan?: number; bus?: GainNode; loop?: boolean; rate?: number } = {}): Promise<() => void> {
    const buf = await this.buffer(path);
    if (!buf) return () => {};
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = !!o.loop;
    if (o.rate) src.playbackRate.value = o.rate;
    const g = this.ctx.createGain();
    g.gain.value = o.volume ?? 1;
    src.connect(g);
    this.route(g, o.pan, o.bus ?? this.sfxBus);
    src.start();
    return () => {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
    };
  }

  private route(g: GainNode, pan: number | undefined, bus: GainNode): void {
    if (pan) {
      const p = this.ctx.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, pan));
      g.connect(p).connect(bus);
    } else g.connect(bus);
  }

  /**
   * Plays a Sounds.txt row (by name or index) under the table's instance, fade and priority
   * rules. Returns the voice, or null when the rules (or a missing row/file) suppress it.
   */
  play(nameOrDef: string | number | SoundDef, o: PlayOpts = {}): Voice | null {
    const head = typeof nameOrDef === 'object' ? nameOrDef : this.sound(nameOrDef);
    if (!head || !this.sounds) return null;
    const live = [...this.voices].filter((v) => v.key === head.index && !v.stopped);
    if (head.deferInst && live.length) return null;
    if (head.stopInst) for (const v of live) v.stop(0);
    const def = this.sounds.pick(head);
    if (!def.file) return null;
    if (!this.claimVoice(head.priority)) return null;
    const g = this.ctx.createGain();
    const level = def.volume * (o.volume ?? 1);
    const now = this.ctx.currentTime;
    g.gain.setValueAtTime(def.fadeIn ? 0 : level, now);
    if (def.fadeIn) g.gain.linearRampToValueAtTime(level, now + def.fadeIn * FRAME_S);
    this.route(g, o.pan, o.bus ?? this.sfxBus);
    const voice = new Voice(def, head.index, g, this.ctx);
    this.voices.add(voice);
    void this.buffer(def.file).then((buf) => {
      if (!buf || voice.stopped) {
        this.voices.delete(voice);
        return;
      }
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.loop = o.loop ?? false;
      src.connect(g);
      src.onended = () => {
        voice.ended = true;
        this.voices.delete(voice);
      };
      voice.src = src;
      src.start();
    });
    return voice;
  }

  /** Plays a row positioned `dx, dy` subtiles from the listener (pan by screen x, distance falloff). */
  playAt(nameOrDef: string | number | SoundDef, dx: number, dy: number, o: PlayOpts = {}): Voice | null {
    const def = typeof nameOrDef === 'object' ? nameOrDef : this.sound(nameOrDef);
    if (!def) return null;
    const p = spatialize(dx, dy, def.falloff, this.separation);
    if (p.volume <= 0.01) return null;
    return this.play(def, { ...o, volume: (o.volume ?? 1) * p.volume, pan: p.pan });
  }

  /** True if a slot is free, or one could be taken from a lower-priority sound. */
  private claimVoice(priority: number): boolean {
    for (const v of this.voices) if (v.stopped || v.ended) this.voices.delete(v);
    if (this.voices.size < MAX_VOICES) return true;
    let low: Voice | null = null;
    for (const v of this.voices) if (!low || v.def.priority < low.def.priority) low = v;
    if (!low || low.def.priority >= priority) return false;
    low.stop(0);
    this.voices.delete(low);
    return true;
  }

  /** Switches background music with a cross-fade. Same track keeps playing. */
  async playMusic(path: string | null, fade = 1.2): Promise<void> {
    if (this.music?.path === path?.toLowerCase()) return;
    const now = this.ctx.currentTime;
    if (this.music) {
      const old = this.music;
      old.gain.gain.cancelScheduledValues(now);
      old.gain.gain.setValueAtTime(old.gain.gain.value, now);
      old.gain.gain.linearRampToValueAtTime(0, now + fade);
      old.src.stop(now + fade + 0.05);
      this.music = null;
    }
    if (!path) return;
    const buf = await this.buffer(path);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, this.ctx.currentTime);
    g.gain.linearRampToValueAtTime(1, this.ctx.currentTime + fade);
    src.connect(g).connect(this.musicBus);
    src.start();
    this.music = { src, gain: g, path: path.toLowerCase() };
  }

  /** NPC speech: one line at a time. Resolves to the line's length in seconds (0 if missing). */
  async speak(path: string): Promise<number> {
    this.stopSpeech();
    const buf = await this.buffer(path);
    if (!buf) return 0;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.speechBus);
    src.start();
    this.speech = src;
    return buf.duration;
  }
  stopSpeech(): void {
    try {
      this.speech?.stop();
    } catch {
      /* ignore */
    }
    this.speech = null;
  }
}
